"use client"

// App.js - COMPLETO CON SESSIONMANAGER INTEGRADO

import { useState, useEffect, useMemo, useCallback } from "react"
import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import "./styles/App.css"
import PortalFondoCafetero from "./PortalMain"
import Login, { REMEMBERED_USER_KEY } from "./Login"
import SessionManager from "./SessionManager"
import logo from "./imgs/Logo_Fcn.png"

// Portal Home component
function PortalHome({ user, applications, onLogout }) {
  return <PortalFondoCafetero user={user} onLogout={onLogout} applications={applications} />
}

// FUNCIÓN PARA LIMPIAR COOKIE DE SESIÓN
const limpiarCookieSesion = () => {
  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
  
  if (isDevelopment) {
    document.cookie = 'sessionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  } else {
    document.cookie = 'sessionId=; domain=.aplicacionesfcn.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
};

// LIMPIA EL STORAGE CONSERVANDO EL AVISO DE SESIÓN EXPIRADA QUE MUESTRA EL LOGIN
// Y LOS ACCESOS RECIENTES Y EL USUARIO RECORDADO (PREFERENCIAS DEL USUARIO, NO DE LA SESIÓN)
const limpiarAlmacenamiento = () => {
  const expirada = sessionStorage.getItem('sessionExpired');
  const motivo = sessionStorage.getItem('sessionExpiredReason');
  const recientes = Object.keys(localStorage)
    .filter((key) => key.startsWith('portalRecentApps:') || key === REMEMBERED_USER_KEY)
    .map((key) => [key, localStorage.getItem(key)]);

  localStorage.clear();
  sessionStorage.clear();

  recientes.forEach(([key, value]) => localStorage.setItem(key, value));

  if (expirada) {
    sessionStorage.setItem('sessionExpired', expirada);
    if (motivo) sessionStorage.setItem('sessionExpiredReason', motivo);
  }
};

// COMPONENTE PRINCIPAL
function AppContent() {
  const [user, setUser] = useState(null)
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [appsLoading, setAppsLoading] = useState(false)

  const API_BASE_URL = useMemo(() => process.env.REACT_APP_API_URL, [])

  // FUNCIÓN PARA CARGAR APLICACIONES
  const loadApplications = useCallback(async (userData) => {
    if (!userData || !userData.token) {
      return []
    }

    setAppsLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/catalogoAplicaciones`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`API respondió con status: ${response.status}`)
      }

      const responseText = await response.text()
      
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        throw new Error("Error parseando respuesta JSON")
      }
      
      const allApps = Array.isArray(data) ? data : data.data || []

      if (allApps.length > 0) {
        const userPermissionIds = userData.permisos?.Aplicaciones?.map((app) => app.Id) || []

        const transformedApps = allApps.map((app) => {
          const hasUserAccess = userPermissionIds.includes(app.Id)
          const hasLink = Boolean(app.Link && app.Link.trim() !== "" && app.Link !== "#placeholder")
          const isSIGEV = app.Id === 10
          const isAdmin = userData.esAdmin === true
          const isInactive = app.Estado === "INACTIVA"

          let finalAccess = false
          if (isAdmin) {
            // ADMIN tiene acceso a TODAS las apps (incluso inactivas)
            finalAccess = hasLink
          } else if (isSIGEV) {
            finalAccess = hasLink && !isInactive
          } else {
            finalAccess = hasLink && hasUserAccess && !isInactive
          }

          return {
            id: app.Id,
            Id: app.Id,
            name: app.NombreAplicacion,
            NombreAplicacion: app.NombreAplicacion,
            Link: app.Link,
            description: app.Descripcion || `${app.NombreAplicacion}`,
            icon: app.Icon || "Settings",
            Icon: app.Icon || "Settings",
            unidadAdministrativa:
              app.UnidadAdministrativa?.Descripcion || app.UnidadAdministrativa || "Sin asignar",
            hasLink: hasLink,
            secureAccess: finalAccess,
            userHasPermission: hasUserAccess || isSIGEV || isAdmin,
            isSIGEV: isSIGEV,
            isAdmin: isAdmin,
            originalUserAccess: hasUserAccess,
            estado: app.Estado || "ACTIVA",
            isInactive: isInactive
          }
        })

        return transformedApps

      } else {
        if (userData.permisos?.Aplicaciones) {
          const fallbackApps = userData.permisos.Aplicaciones.map((app) => ({
            id: app.Id,
            Id: app.Id,
            name: app.NombreAplicacion,
            NombreAplicacion: app.NombreAplicacion,
            Link: app.Link || "#",
            description: app.Descripcion || `Sistema de gestión para ${app.NombreAplicacion}`,
            icon: app.Icon || "Settings",
            Icon: app.Icon || "Settings",
            unidadAdministrativa: app.UnidadAdministrativa?.Descripcion || app.UnidadAdministrativa || "Sin asignar",
            hasLink: Boolean(app.Link && app.Link.trim() !== "" && app.Link !== "#placeholder"),
            secureAccess: Boolean(app.Link && app.Link.trim() !== "" && app.Link !== "#placeholder"),
            userHasPermission: true,
            isSIGEV: app.Id === 10,
            isAdmin: userData.esAdmin === true,
            originalUserAccess: true,
            estado: app.Estado || "ACTIVA",
            isInactive: app.Estado === "INACTIVA"
          }))
          return fallbackApps
        }
        return []
      }

    } catch (apiError) {
      if (userData.permisos?.Aplicaciones) {
        const fallbackApps = userData.permisos.Aplicaciones.map((app) => ({
          id: app.Id,
          Id: app.Id,
          name: app.NombreAplicacion,
          NombreAplicacion: app.NombreAplicacion,
          Link: app.Link || "#",
          description: app.Descripcion || `Sistema de gestión para ${app.NombreAplicacion}`,
          icon: app.Icon || "Settings",
          Icon: app.Icon || "Settings",
          unidadAdministrativa: app.UnidadAdministrativa?.Descripcion || app.UnidadAdministrativa || "Sin asignar",
          hasLink: Boolean(app.Link && app.Link.trim() !== "" && app.Link !== "#placeholder"),
          secureAccess: Boolean(app.Link && app.Link.trim() !== "" && app.Link !== "#placeholder"),
          userHasPermission: true,
          isSIGEV: app.Id === 10,
          isAdmin: userData.esAdmin === true,
          originalUserAccess: true,
          estado: app.Estado || "ACTIVA",
          isInactive: app.Estado === "INACTIVA"
        }))
        return fallbackApps
      }
      return []
    } finally {
      setAppsLoading(false)
    }
  }, [API_BASE_URL])

  // CARGAR USUARIO INICIAL DESDE LOCALSTORAGE
  useEffect(() => {
    const loadInitialUser = async () => {
      try {
        const savedUser = localStorage.getItem("user")
        const savedToken = localStorage.getItem("authToken")

        if (!savedUser || !savedToken) {
          setLoading(false)
          return
        }

        const userData = JSON.parse(savedUser)
        userData.token = savedToken
        
        setUser(userData)
        const apps = await loadApplications(userData)
        setApplications(apps)

      } catch (error) {
        setError("Error al cargar datos del usuario")
      } finally {
        setLoading(false)
      }
    }

    loadInitialUser()
  }, [loadApplications])

  // MANEJAR LOGIN
  const handleLogin = useCallback(async (userData) => {
    try {
      const { token, ...userInfo } = userData

      if (!userData || !userData.usuario || !token) {
        throw new Error("Datos de usuario o token inválidos")
      }

      setUser(userData)
      setError("")

      const apps = await loadApplications(userData)
      setApplications(apps)

      const sessionData = {
        ...userInfo,
        loginTime: new Date().getTime(),
      }

      localStorage.setItem("user", JSON.stringify(sessionData))
      localStorage.setItem("authToken", token)

    } catch (error) {
      setError("Error al procesar el login")
    }
  }, [loadApplications])

  // MANEJAR LOGOUT
  const handleLogout = useCallback(async () => {
    try {
      if (user?.token && API_BASE_URL) {
        // Tope de 5 s: si el servidor no responde, el cierre local sigue igual y el modal no queda esperando
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          })
        } catch (logoutError) {
          // Continuar con logout local aunque falle el servidor
        } finally {
          clearTimeout(timeoutId)
        }
      }

      limpiarCookieSesion();
      limpiarAlmacenamiento()
      setUser(null)
      setApplications([])
      setError("")

    } catch (error) {
      limpiarCookieSesion();
      limpiarAlmacenamiento()
      setUser(null)
      setApplications([])
      setError("")
    }
  }, [user, API_BASE_URL])

  // LOADING
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF8F4] px-6" role="status" aria-live="polite">
        <div className="flex flex-col items-center text-center">
          <img src={logo} alt="Logo Fondo Cafetero" className="login-rise h-24 w-auto" style={{ "--i": 0 }} />
          {/* Barra fina indeterminada en lugar del spinner */}
          <div className="login-rise mt-8 h-0.5 w-40 overflow-hidden rounded-full bg-cafe-100/60" style={{ "--i": 1 }}>
            <span className="portal-loader-bar block h-full w-1/3 rounded-full bg-cafe-500" />
          </div>
          <p className="login-rise mt-5 text-sm text-neutral-300" style={{ "--i": 2 }}>
            {appsLoading ? "Cargando aplicaciones" : "Iniciando sesión"}
          </p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Login onLogin={handleLogin} initialError={error} onErrorCleared={() => setError("")} />
  }

  // ENVOLVER CON SESSIONMANAGER
  return (
    <SessionManager user={user} onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<PortalHome user={user} applications={applications} onLogout={handleLogout} />} />
        
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                <p className="text-gray-600 mb-6">Página no encontrada</p>
                <button
                  onClick={() => (window.location.href = "/")}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg"
                >
                  Volver al Portal
                </button>
              </div>
            </div>
          }
        />
      </Routes>
    </SessionManager>
  )
}

function App() {
  return (
    <div className="App">
      <Router>
        <AppContent />
      </Router>
    </div>
  )
}

export default App